// Trade.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../api";
import BackButton from "./common/BackButton";
import { SEMANTIC, UI } from "../styles/theme";

/* ======================================================
   Helpers
====================================================== */
const formatMoneyUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const formatDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleString();
};

const TERMINAL_STATUSES = [
  "filled",
  "canceled",
  "rejected",
  "expired",
  "timeout",
  "error",
];

const PENDING_STATUSES = [
  "created", "submitted", "queued", "new",
  "partial", "accepted", "pending_new",
];

/* ======================================================
   Component
====================================================== */
export default function Trade() {
  const userId = localStorage.getItem("userId");
  const pollingRef = useRef(null);
  const { t } = useTranslation();

  const [userName, setUserName] = useState("");
  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(null);

  const [liquidity, setLiquidity] = useState(0);
  const [tickers, setTickers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [orders, setOrders] = useState([]);

  const [side, setSide] = useState("buy");
  const [symbol, setSymbol] = useState("");
  const [qty, setQty] = useState("");

  const [allTickers, setAllTickers] = useState([]);       // [{ ticker, name, has_logo }]
  const [tickerMap, setTickerMap] = useState({});          // { AAPL: { name, has_logo } }
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [tickerInfo, setTickerInfo] = useState(null);      // { symbol, name, logo_url }
  const symbolInputRef = useRef(null);

  /* ======================================================
     LOAD TICKER LIST (autocomplete)
  ====================================================== */
  useEffect(() => {
    API.get("/tickers/list")
      .then((res) => {
        const data = res.data || [];
        setAllTickers(data.map((t) => t.ticker));
        const map = {};
        data.forEach((t) => { map[t.ticker] = { name: t.name, has_logo: t.has_logo }; });
        setTickerMap(map);
      })
      .catch(() => {});
  }, []);

  const handleSymbolChange = (e) => {
    const val = e.target.value.toUpperCase();
    setSymbol(val);
    setTickerInfo(null);
    if (val.length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      const filtered = allTickers.filter((t) => t.startsWith(val)).slice(0, 10);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    }
  };

  const selectSuggestion = (ticker) => {
    setSymbol(ticker);
    setSuggestions([]);
    setShowSuggestions(false);
    showTickerInfo(ticker);
  };

  const showTickerInfo = (sym) => {
    if (!sym || !allTickers.includes(sym)) { setTickerInfo(null); return; }
    const meta = tickerMap[sym];
    setTickerInfo({
      symbol: sym,
      name: meta?.name || sym,
      logo_url: `${process.env.PUBLIC_URL}/logos/${sym}.png`,
    });
  };

  const handleSymbolBlur = () => {
    setTimeout(() => setShowSuggestions(false), 150);
    showTickerInfo(symbol);
  };

  const isValidSymbol = symbol === "" || (allTickers.length > 0 && allTickers.includes(symbol));

  const [activeTab, setActiveTab] = useState("orders");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [cancelConfirm, setCancelConfirm] = useState(null); // { id, symbol }

  /* ======================================================
     LOAD USER + PORTFOLIOS
  ====================================================== */
  useEffect(() => {
    if (!userId) return;

    async function load() {
      const userRes = await API.get(`/users/${userId}`);
      setUserName(userRes.data.nickname);

      const pfRes = await API.get(`/portfolios/${userId}`);
      const pf = pfRes.data.portfolios || [];
      setPortfolios(pf);

      if (pf.length > 0) setSelectedPortfolioId(pf[0].id);
    }

    load();
  }, [userId]);

  /* ======================================================
     LOAD PORTFOLIO DATA (FULL)
  ====================================================== */
  const refreshPortfolio = useCallback(async () => {
    if (!selectedPortfolioId) return;

    const pRes = await API.get(`/portfolio/by_id/${selectedPortfolioId}`);
    setLiquidity(Number(pRes.data?.portfolio?.liquidity || 0));

    const tRes = await API.get(`/portfolio/${selectedPortfolioId}/tickers`);
    setTickers(tRes.data?.tickers || []);

    const trRes = await API.get(
      `/portfolio/${selectedPortfolioId}/transactions`
    );
    setTransactions(trRes.data?.transactions || []);

    const oRes = await API.get(
      `/portfolio/${selectedPortfolioId}/orders`
    );
    setOrders(oRes.data?.orders || []);
  }, [selectedPortfolioId]);

  useEffect(() => {
    refreshPortfolio();
  }, [refreshPortfolio]);

  /* ======================================================
     AUTO-REFRESH ORDERS (SMART POLLING)
  ====================================================== */
  useEffect(() => {
    if (!selectedPortfolioId) return;
    if (activeTab !== "orders") return;

    // clear previous polling if any
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    pollingRef.current = setInterval(async () => {
      try {
        const res = await API.get(
          `/portfolio/${selectedPortfolioId}/orders`
        );
        const newOrders = res.data?.orders || [];
        setOrders(newOrders);

        const hasOpenOrders = newOrders.some(
          (o) => !TERMINAL_STATUSES.includes(o.status)
        );

        // stop polling if everything is terminal
        if (!hasOpenOrders) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } catch {
        // silent fail → next tick will retry
      }
    }, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [selectedPortfolioId, activeTab]);

  /* ======================================================
     CANCEL ORDER
  ====================================================== */
  const onCancelOrder = async (internalOrderId) => {
    try {
      await API.post(`/orders/${internalOrderId}/cancel`);
      setCancelConfirm(null);
      await refreshPortfolio();
    } catch (e) {
      setError("Errore durante l'annullamento dell'ordine.");
      setCancelConfirm(null);
    }
  };

  /* ======================================================
     SUBMIT ORDER → ALPACA
  ====================================================== */
  const onSubmit = async () => {
    setError("");
    setOk("");

    if (!symbol || !qty || Number(qty) <= 0) {
      setError(t("trade.error_invalid_data"));
      return;
    }

    try {
      setLoading(true);

      await API.post("/orders", {
        portfolio_id: selectedPortfolioId,
        symbol,
        side,
        quantity: Number(qty),
        order_type: "market",
      });

      setOk(t("trade.success_order", { side: side.toUpperCase() }));
      setSymbol("");
      setQty("");
      setTickerInfo(null);

      // force refresh + restart polling
      await refreshPortfolio();
    } catch (e) {
      console.error(e);
      setError(t("trade.error_order"));
    } finally {
      setLoading(false);
    }
  };

  /* ======================================================
     RENDER
  ====================================================== */
  const CANCELABLE_STATUSES = ["created", "submitted", "queued", "new", "partial", "accepted", "pending_new"];

  return (
    <div style={styles.page}>
      <BackButton />
      <h2 style={styles.title}>{t("trade.title", { name: userName })}</h2>

      {/* PORTFOLIO */}
      <div style={styles.center}>
        <select
          value={selectedPortfolioId || ""}
          onChange={(e) => setSelectedPortfolioId(Number(e.target.value))}
          style={styles.select}
        >
          {portfolios.map((p) => (
            <option key={p.id} value={p.id}>
              {p.Nome_tipo_modello || `Portfolio ${p.id}`}
            </option>
          ))}
        </select>
      </div>

      {/* INFO */}
      <div style={styles.infoCard}>
        <div>
          <strong>{t("trade.liquidity")}</strong>{" "}
          <span style={{ color: SEMANTIC.actionBuy }}>
            {formatMoneyUSD(liquidity)}
          </span>
        </div>
        <div style={styles.infoSub}>
          <strong>{t("trade.tickers")}</strong>{" "}
          {tickers.length === 0
            ? "—"
            : tickers.map((t) => `${t.ticker} (${t.quantity})`).join("   ")}
        </div>
        {(() => {
          const pending = orders.filter(o => PENDING_STATUSES.includes(o.status));
          if (pending.length === 0) return null;
          return (
            <div style={{ marginTop: UI.spacing.sm, fontSize: UI.fontSize.sm, borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: UI.spacing.sm }}>
              <strong style={{ color: "#ffaa00" }}>Pending orders</strong>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                {pending.map(o => (
                  <span key={o.internal_order_id} style={{
                    backgroundColor: "rgba(255,170,0,0.1)",
                    border: "1px solid rgba(255,170,0,0.35)",
                    borderRadius: UI.borderRadius.pill,
                    padding: "3px 10px",
                    color: "#ffaa00",
                    fontSize: "12px",
                  }}>
                    {o.side.toUpperCase()} {o.symbol} × {o.quantity} — {o.status}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* FORM */}
      <div style={styles.formCard}>
        <div style={styles.pillWrap}>
          <button
            onClick={() => setSide("buy")}
            style={pillStyle(side === "buy", SEMANTIC.toggleBuy)}
          >
            {t("trade.buy")}
          </button>
          <button
            onClick={() => setSide("sell")}
            style={pillStyle(side === "sell", SEMANTIC.toggleSell)}
          >
            {t("trade.sell")}
          </button>
        </div>

        <div style={styles.inputRow}>
          <div style={{ flex: 3, position: "relative" }}>
            <input
              ref={symbolInputRef}
              placeholder={t("trade.ticker_placeholder")}
              value={symbol}
              onChange={handleSymbolChange}
              onBlur={handleSymbolBlur}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              style={{
                ...styles.input,
                width: "100%",
                borderColor: symbol && !isValidSymbol ? "#ff6b6b" : undefined,
              }}
            />
            {symbol && !isValidSymbol && (
              <div style={{ color: "#ff6b6b", fontSize: "11px", marginTop: 3 }}>
                Ticker non valido
              </div>
            )}
            {showSuggestions && (
              <ul style={styles.suggestionList}>
                {suggestions.map((ticker) => (
                  <li
                    key={ticker}
                    onMouseDown={() => selectSuggestion(ticker)}
                    style={styles.suggestionItem}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#2a2a4a"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                  >
                    {ticker}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <input
            placeholder={t("trade.quantity_placeholder")}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            disabled={!symbol || !isValidSymbol}
            style={{
              ...styles.input,
              flex: 2,
              opacity: symbol && isValidSymbol ? 1 : 0.4,
              cursor: symbol && isValidSymbol ? "text" : "not-allowed",
            }}
          />
        </div>

        {/* Ticker info (logo + nome) */}
        {tickerInfo && (
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            marginBottom: 12, padding: "8px 12px",
            backgroundColor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: UI.borderRadius.md,
          }}>
            {tickerInfo.logo_url ? (
              <img
                src={tickerInfo.logo_url}
                alt={tickerInfo.symbol}
                style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 4, backgroundColor: "#fff", padding: 2 }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : (
              <div style={{ width: 32, height: 32, borderRadius: 4, backgroundColor: "#2a2a4a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: "bold", color: SEMANTIC.actionBuy }}>
                {tickerInfo.symbol?.[0]}
              </div>
            )}
            <div>
              <div style={{ fontWeight: "bold", fontSize: UI.fontSize.sm, color: SEMANTIC.textPrimary }}>{tickerInfo.symbol}</div>
              <div style={{ fontSize: "12px", color: SEMANTIC.textSecondary }}>{tickerInfo.name}</div>
            </div>
          </div>
        )}

        <button
          onClick={onSubmit}
          disabled={loading || !isValidSymbol || !symbol}
          style={{
            ...styles.cta,
            background:
              side === "buy"
                ? SEMANTIC.actionBuy
                : SEMANTIC.actionSell,
            color: SEMANTIC.ctaPrimaryText,
          }}
        >
          {side === "buy" ? t("trade.execute_buy") : t("trade.execute_sell")}
        </button>

        {error && <div style={styles.err}>{error}</div>}
        {ok && <div style={styles.ok}>{ok}</div>}
      </div>

      {/* TABS */}
      <div style={styles.tabs}>
        <span
          onClick={() => setActiveTab("orders")}
          style={tabStyle(activeTab === "orders")}
        >
          {t("trade.tab_orders")}
        </span>
        <span
          onClick={() => setActiveTab("transactions")}
          style={tabStyle(activeTab === "transactions")}
        >
          {t("trade.tab_transactions")}
        </span>
      </div>

      {/* CONFIRM CANCEL DIALOG */}
      {cancelConfirm && (
        <div style={styles.overlay}>
          <div style={styles.dialog}>
            <h4 style={{ marginBottom: 12, color: SEMANTIC.textPrimary }}>Annulla ordine</h4>
            <p style={{ color: SEMANTIC.textSecondary, marginBottom: 20 }}>
              Sei sicuro di voler annullare l'ordine su <strong style={{ color: SEMANTIC.actionSell }}>{cancelConfirm.symbol}</strong>?
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setCancelConfirm(null)}
                style={{ ...styles.dialogBtn, background: "#333", color: SEMANTIC.textPrimary }}
              >
                No, mantieni
              </button>
              <button
                onClick={() => onCancelOrder(cancelConfirm.id)}
                style={{ ...styles.dialogBtn, background: SEMANTIC.actionSell, color: "#fff" }}
              >
                Sì, annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TABLE */}
      <div style={styles.tableCard}>
        {activeTab === "orders" ? (
          orders.length === 0 ? (
            <div style={styles.emptyText}>{t("trade.no_orders")}</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th>{t("trade.col_date")}</th>
                  <th>{t("trade.col_symbol")}</th>
                  <th>{t("trade.col_side")}</th>
                  <th>{t("trade.col_qty")}</th>
                  <th>{t("trade.col_status")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.internal_order_id}>
                    <td>{formatDateTime(o.created_at)}</td>
                    <td>{o.symbol}</td>
                    <td>{o.side}</td>
                    <td>{o.quantity}</td>
                    <td>{o.status}</td>
                    <td style={{ textAlign: "center" }}>
                      {CANCELABLE_STATUSES.includes(o.status) && (
                        <button
                          onClick={() => setCancelConfirm({ id: o.internal_order_id, symbol: o.symbol })}
                          title="Annulla ordine"
                          style={styles.cancelBtn}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : transactions.length === 0 ? (
          <div style={styles.emptyText}>{t("trade.no_transactions")}</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th>{t("trade.col_date")}</th>
                <th>{t("trade.col_side")}</th>
                <th>{t("trade.col_symbol")}</th>
                <th>{t("trade.col_qty")}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t, i) => (
                <tr key={i}>
                  <td>{formatDateTime(t.created_at)}</td>
                  <td>{t.action_type}</td>
                  <td>{t.stock_name}</td>
                  <td>{t.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ======================================================
   Styles
====================================================== */
const styles = {
  page: {
    background: SEMANTIC.backgroundPage,
    minHeight: "100vh",
    padding: UI.spacing.xl,
    color: SEMANTIC.textPrimary,
  },
  title: { textAlign: "center", marginBottom: UI.spacing.lg },
  center: { textAlign: "center", marginBottom: UI.spacing.lg },
  select: { padding: "10px", borderRadius: UI.borderRadius.md },

  infoCard: {
    maxWidth: 760,
    margin: "0 auto",
    background: SEMANTIC.backgroundCard,
    padding: UI.spacing.lg,
    borderRadius: UI.borderRadius.lg,
  },
  infoSub: {
    marginTop: UI.spacing.sm,
    fontSize: UI.fontSize.sm,
    color: SEMANTIC.textSecondary,
  },

  formCard: {
    maxWidth: 600,
    margin: "40px auto",
    background: SEMANTIC.backgroundCard,
    padding: UI.spacing.lg,
    borderRadius: UI.borderRadius.lg,
  },

  pillWrap: {
    display: "flex",
    justifyContent: "center",
    gap: 6,
    marginBottom: 16,
    background: "rgba(0,0,0,0.35)",
    padding: 4,
    borderRadius: UI.borderRadius.pill,
  },

  inputRow: {
    display: "flex",
    gap: 10,
    marginBottom: 10,
  },

  input: {
    padding: "10px",
    borderRadius: UI.borderRadius.md,
  },

  cta: {
    width: "100%",
    padding: 12,
    borderRadius: UI.borderRadius.pill,
    border: "none",
    fontWeight: "bold",
    cursor: "pointer",
  },

  tabs: {
    maxWidth: 760,
    margin: "0 auto 12px",
    display: "flex",
    justifyContent: "center",
    gap: 30,
    fontWeight: "bold",
    cursor: "pointer",
  },

  tableCard: {
    maxWidth: 760,
    margin: "0 auto",
    background: "rgba(0,0,0,0.25)",
    padding: UI.spacing.lg,
    borderRadius: UI.borderRadius.lg,
  },

  table: { width: "100%" },
  emptyText: { textAlign: "center", opacity: 0.6 },

  err: { marginTop: 10, color: "#ff6b6b" },
  ok: { marginTop: 10, color: "#51cf66" },

  cancelBtn: {
    background: "transparent",
    border: "1px solid #ff6b6b",
    color: "#ff6b6b",
    borderRadius: "50%",
    width: 24,
    height: 24,
    fontSize: 12,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    padding: 0,
  },

  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },

  dialog: {
    backgroundColor: SEMANTIC.backgroundCard,
    borderRadius: UI.borderRadius.lg,
    padding: "32px 40px",
    textAlign: "center",
    minWidth: 300,
    maxWidth: 400,
    boxShadow: "0 8px 24px rgba(0,0,0,0.8)",
  },

  dialogBtn: {
    padding: "10px 24px",
    borderRadius: UI.borderRadius.pill,
    border: "none",
    fontWeight: "bold",
    cursor: "pointer",
    fontSize: UI.fontSize.md,
  },

  suggestionList: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "#1a1a2e",
    border: "1px solid #444",
    borderRadius: UI.borderRadius.md,
    marginTop: 2,
    zIndex: 100,
    listStyle: "none",
    padding: 0,
    margin: 0,
    maxHeight: 220,
    overflowY: "auto",
  },

  suggestionItem: {
    padding: "8px 12px",
    cursor: "pointer",
    color: SEMANTIC.textPrimary,
    fontSize: UI.fontSize.sm,
    fontFamily: "monospace",
    backgroundColor: "transparent",
    transition: "background-color 0.1s",
  },
};

const pillStyle = (active, bg) => ({
  minWidth: 72,
  textAlign: "center",
  padding: "6px 18px",
  borderRadius: UI.borderRadius.pill,
  fontWeight: 700,
  cursor: "pointer",
  userSelect: "none",
  backgroundColor: active ? bg : "transparent",
  color: active ? "#000" : "rgba(255,255,255,0.55)",
  transition: "all 0.15s ease",
});

const tabStyle = (active) => ({
  color: active ? SEMANTIC.actionBuy : SEMANTIC.textSecondary,
  borderBottom: active ? `2px solid ${SEMANTIC.actionBuy}` : "none",
  paddingBottom: 4,
});
